import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Unlink, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const box = "rounded-2xl border border-slate-200 bg-white p-5";
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const time = (v) => new Date(v).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });

export const useLinks = (enabled = true) => {
  const { business } = useAuth();
  const [links, setLinks] = useState({ linked: [], candidates: [] });
  const load = useCallback(() => {
    if (!enabled) return;
    api.get("/links").then((r) => setLinks(r.data)).catch(() => setLinks({ linked: [], candidates: [] }));
  }, [enabled]);
  useEffect(() => { load(); }, [load, business?.business_id]);
  return { ...links, reload: load };
};

export const LinkedSettings = () => {
  const { business } = useAuth();
  const { linked, candidates, reload } = useLinks();
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(formatError(e)); } };
  return (
    <div className={box} data-testid="linked-settings">
      <p className="font-semibold text-[#0B2D5B]">Linked businesses</p>
      <p className="mt-1 text-sm text-slate-600">Link businesses you own so staff can work at both with one login. Each business keeps its own timesheets, payroll, reports, rosters, expenses and billing.</p>
      <div className="mt-4 space-y-2">
        {linked.filter((b) => b.business_id !== business.business_id).map((b) => (
          <div key={b.business_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#00C16B]/40 bg-[#00C16B]/5 p-3" data-testid={`linked-${b.business_id}`}>
            <span className="flex min-w-0 items-center gap-2 break-words font-medium"><Link2 className="h-4 w-4 shrink-0 text-[#00A85C]" />{b.name}</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => run(() => api.delete(`/links/${b.business_id}`), `Unlinked ${b.name}`)} data-testid={`unlink-${b.business_id}`}><Unlink className="mr-1 h-4 w-4" />Unlink</Button>
          </div>
        ))}
        {candidates.map((b) => (
          <div key={b.business_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 p-3">
            <span className="min-w-0 break-words font-medium">{b.name}</span>
            <Button size="sm" className="rounded-full bg-[#00C16B] hover:bg-[#00A85C]" onClick={() => run(() => api.post("/links", { business_id: b.business_id }), `Linked ${b.name}`)} data-testid={`link-${b.business_id}`}><Link2 className="mr-1 h-4 w-4" />Link</Button>
          </div>
        ))}
        {!candidates.length && linked.length < 2 && <p className="text-sm text-slate-500">You don't own any other business to link yet.</p>}
      </div>
    </div>
  );
};

const LinkedRow = ({ mid, b, onSaved }) => {
  const m = b.membership;
  const on0 = !!m && m.status === "active";
  const [f, setF] = useState({ enabled: on0, role: m?.role === "manager" ? "manager" : "employee", workplace_ids: m?.workplace_ids || [], job_role_id: m?.job_role_id || "", department_id: m?.department_id || "", employment_type: m?.employment_type || "casual", hourly_rate: m?.hourly_rate ?? "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const groups = (kind) => b.groups.filter((g) => g.kind === kind);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/members/${mid}/linked/${b.business_id}`, { ...f, job_role_id: f.job_role_id || null, department_id: f.department_id || null, hourly_rate: f.hourly_rate === "" ? null : Number(f.hourly_rate) });
      toast.success(f.enabled ? `Saved access to ${b.name}` : `Removed access to ${b.name}`);
      onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const pick = (k, kind, label) => (
    <Select value={f[k] || "none"} onValueChange={(v) => set(k, v === "none" ? "" : v)}><SelectTrigger className="h-10 rounded-xl" data-testid={`linked-${k}-${b.business_id}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent><SelectItem value="none">No {label.toLowerCase()}</SelectItem>{groups(kind).map((g) => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}</SelectContent></Select>
  );
  return (
    <div className={`rounded-xl border p-4 ${f.enabled ? "border-[#00C16B]/50" : "border-slate-200"}`} data-testid={`linked-access-${b.business_id}`}>
      <label className="flex items-center gap-3 font-semibold text-[#0B2D5B]"><Checkbox checked={f.enabled} onCheckedChange={(v) => set("enabled", !!v)} data-testid={`linked-enable-${b.business_id}`} /><span className="min-w-0 break-words">Works at {b.name}</span></label>
      {f.enabled && (
        <div className="mt-3 space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">{b.workplaces.map((w) => (
            <label key={w.workplace_id} className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm"><Checkbox checked={f.workplace_ids.includes(w.workplace_id)} onCheckedChange={() => set("workplace_ids", f.workplace_ids.includes(w.workplace_id) ? f.workplace_ids.filter((x) => x !== w.workplace_id) : [...f.workplace_ids, w.workplace_id])} data-testid={`linked-wp-${w.workplace_id}`} /><span className="min-w-0 break-words">{w.name}</span></label>))}</div>
          <p className="text-xs text-slate-500">Leave workplaces unticked to allow all of {b.name}'s workplaces.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {pick("job_role_id", "job_role", "Job role")}
            {pick("department_id", "department", "Department")}
            <Select value={f.employment_type} onValueChange={(v) => set("employment_type", v)}><SelectTrigger className="h-10 rounded-xl" data-testid={`linked-emp-type-${b.business_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{[["casual", "Casual"], ["part_time", "Part time"], ["full_time", "Full time"], ["contractor", "Contractor"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
            <Select value={f.role} onValueChange={(v) => set("role", v)}><SelectTrigger className="h-10 rounded-xl" data-testid={`linked-role-${b.business_id}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="employee">Employee access</SelectItem><SelectItem value="manager">Manager access</SelectItem></SelectContent></Select>
            <Input type="number" min="0" step="0.01" placeholder={`Hourly rate at ${b.name}`} value={f.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} className="h-10 rounded-xl" data-testid={`linked-rate-${b.business_id}`} />
          </div>
          <p className="text-xs text-slate-500">Payment method, super, tax and other payroll settings for {b.name} are set on this person's profile inside {b.name}.</p>
        </div>
      )}
      <Button className="mt-3 h-10 rounded-full bg-[#00C16B] hover:bg-[#00A85C]" disabled={busy || (!f.enabled && !on0)} onClick={save} data-testid={`linked-save-${b.business_id}`}>{busy ? "Saving…" : "Save"}</Button>
    </div>
  );
};

export const LinkedAccess = ({ m }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/members/${m.membership_id}/linked`).then((r) => setD(r.data)).catch(() => setD(null)), [m.membership_id]);
  useEffect(() => { load(); }, [load]);
  if (!d || !d.businesses.length || m.role === "owner") return null;
  return (
    <div className={box} data-testid="emp-linked-access">
      <p className="font-semibold text-[#0B2D5B]">Assigned Businesses & Workplaces</p>
      <p className="mb-3 mt-1 text-sm text-slate-600">Let {m.name} work at your linked businesses with the same login. Hours and pay are recorded separately for each business.</p>
      {!d.has_login ? <p className="text-sm text-[#B35F00]">{m.name} needs a Clockaroo login first. Send them an invite, then come back here.</p>
        : <div className="space-y-3">{d.businesses.map((b) => <LinkedRow key={`${b.business_id}-${b.membership?.status}-${b.membership?.hourly_rate}`} mid={m.membership_id} b={b} onSaved={load} />)}</div>}
    </div>
  );
};

export const LinkedOverview = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const go = () => api.get("/linked/overview").then((r) => setD(r.data)).catch((e) => toast.error(formatError(e)));
    go();
    const i = setInterval(go, 60000);
    return () => clearInterval(i);
  }, []);
  if (!d) return <div className="h-48 animate-pulse rounded-2xl bg-white" data-testid="linked-overview-loading" />;
  return (
    <div className="space-y-5" data-testid="linked-overview">
      <div className={box}>
        <p className="flex items-center gap-2 font-semibold text-[#0B2D5B]"><Users className="h-5 w-5 text-[#00A85C]" />Working now ({d.working.length})</p>
        {!d.working.length ? <p className="mt-2 text-sm text-slate-500">Nobody is clocked in at your linked businesses.</p> : (
          <div className="mt-3 space-y-2">{d.working.map((w, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3 text-sm" data-testid={`linked-working-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2"><span className="break-words font-bold text-[#0B2D5B]">{w.name}</span><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${w.status === "Working" ? "bg-[#00C16B]/15 text-[#00A85C]" : "bg-[#FF8A00]/15 text-[#B35F00]"}`}>{w.status}</span></div>
              <p className="mt-1 break-words text-slate-600">{w.business_name}{w.workplace && w.workplace !== w.business_name ? ` · ${w.workplace}` : ""}</p>
              <p className="break-words text-xs text-slate-500">{[w.job_role, w.department].filter(Boolean).join(" · ") || "No role set"}</p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                <span>In: <b>{time(w.clock_in)}</b></span><span>Hours: <b>{w.worked_hours}</b></span><span>Rate: <b>{money(w.hourly_rate)}</b></span><span>Est. cost: <b>{money(w.est_cost)}</b></span>
              </div>
            </div>))}</div>
        )}
      </div>
      <div className={box} data-testid="linked-week-totals">
        <p className="font-semibold text-[#0B2D5B]">This week ({d.week.start} to {d.week.end})</p>
        <div className="mt-3 space-y-2 text-sm">
          {d.businesses.map((b) => <div key={b.business_id} className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-2"><span className="min-w-0 break-words">{b.name}</span><span className="font-mono-num">{b.worked_hours} h · {money(b.est_gross)}</span></div>)}
          <div className="flex flex-wrap justify-between gap-2 font-bold text-[#0B2D5B]" data-testid="linked-combined"><span>Combined (view only)</span><span className="font-mono-num">{d.combined.worked_hours} h · {money(d.combined.est_gross)}</span></div>
        </div>
        {d.shared_people.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-bold uppercase text-slate-500">Staff working at more than one business</p>
            {d.shared_people.map((p, i) => <div key={i} className="mt-2 rounded-xl bg-slate-50 p-3 text-sm" data-testid={`linked-shared-${i}`}><p className="font-semibold">{p.name}: {p.worked_hours} h · {money(p.est_gross)}</p>{p.by_business.map((x, j) => <p key={j} className="text-xs text-slate-600">{x.name}: {x.worked_hours} h · {money(x.est_gross)}</p>)}</div>)}
          </div>
        )}
        <p className="mt-3 text-xs text-slate-500">Each business's payroll and reports stay separate. Combined figures are for your overview only.</p>
      </div>
    </div>
  );
};
